import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, ShoppingCart, Loader2 } from 'lucide-react';
import {
  selectCartItems,
  selectCartTotalAmount,
  selectCartTotalQuantity,
  selectCartIsOpen,
  closeCart,
  updateQuantity,
  removeFromCart,
  clearCart,
} from '../store/slices/cartSlice';
import { showToast } from '../store/slices/toastSlice';
import { apiFetch } from '../utils/api';
import { pushAddToCart, pushRemoveFromCart } from '../utils/gtm';

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const totalAmount = useSelector(selectCartTotalAmount);
  const totalQuantity = useSelector(selectCartTotalQuantity);
  const isOpen = useSelector(selectCartIsOpen);

  // Track which item keys are currently being stock-checked
  const [checking, setChecking] = useState({});

  const itemKey = (item) => `${item.id}-${item.size}`;

  const handleDecrease = (item) => {
    if (item.quantity > 1) {
      dispatch(updateQuantity({ id: item.id, size: item.size, quantity: item.quantity - 1 }));
      pushRemoveFromCart([{ ...item, quantity: 1 }], item.price);
    }
  };

  const handleIncrease = useCallback(async (item) => {
    const key = itemKey(item);
    setChecking(prev => ({ ...prev, [key]: true }));

    try {
      const res = await apiFetch(`/api/public/products/${item.id}/`);
      const data = await res.json();

      // Find the matching active variant for this size
      const variant = Array.isArray(data.variants)
        ? data.variants.find(v => v.size === item.size && v.is_active !== false)
        : null;

      // Fall back to overall stock if no size-specific variant found
      const available = variant != null ? (variant.stock_quantity ?? 0) : (data.in_stock ? 9999 : 0);

      if (item.quantity >= available) {
        dispatch(showToast({
          message: `Maximum ${available} piece${available !== 1 ? 's' : ''} available in size ${item.size}`,
          type: 'error',
        }));
      } else {
        dispatch(updateQuantity({ id: item.id, size: item.size, quantity: item.quantity + 1 }));
        pushAddToCart(item, 1);
      }
    } catch {
      // Network error — allow the increase optimistically
      dispatch(updateQuantity({ id: item.id, size: item.size, quantity: item.quantity + 1 }));
      pushAddToCart(item, 1);
    } finally {
      setChecking(prev => ({ ...prev, [key]: false }));
    }
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => dispatch(closeCart())}
      />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-sm sm:max-w-md bg-white shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Your Cart</h2>
            {totalQuantity > 0 && (
              <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {totalQuantity} {totalQuantity === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          <button
            onClick={() => dispatch(closeCart())}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cartItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <ShoppingBag className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">Your cart is empty</h3>
              <p className="text-sm text-gray-500 mb-5">
                Add items to your cart to continue shopping
              </p>
              <button
                onClick={() => dispatch(closeCart())}
                className="px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-full hover:bg-primary-700 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const key = itemKey(item);
                const isChecking = !!checking[key];
                return (
                  <div key={key} className="flex gap-3 p-3 bg-gray-50 rounded-xl">
                    {/* Image */}
                    <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-200">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">
                        {item.name}
                      </h3>
                      <span className="inline-block text-xs text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded mt-0.5 mb-2">
                        Size: {item.size}
                      </span>

                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900">
                          ৳{(item.price * item.quantity).toLocaleString()}
                        </span>

                        <div className="flex items-center gap-1">
                          {/* Decrease */}
                          <button
                            onClick={() => handleDecrease(item)}
                            disabled={isChecking}
                            className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            <Minus className="w-3 h-3" />
                          </button>

                          {/* Quantity */}
                          <span className="w-7 text-center text-sm font-medium">{item.quantity}</span>

                          {/* Increase — triggers stock check */}
                          <button
                            onClick={() => handleIncrease(item)}
                            disabled={isChecking}
                            className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            {isChecking
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Plus className="w-3 h-3" />}
                          </button>

                          {/* Remove */}
                          <button
                            onClick={() => {
                              dispatch(removeFromCart({ id: item.id, size: item.size }));
                              pushRemoveFromCart([item], item.price * item.quantity);
                            }}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors ml-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-100 px-5 py-4 space-y-3 bg-white">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Subtotal ({totalQuantity} {totalQuantity === 1 ? 'item' : 'items'})
              </span>
              <span className="text-lg font-bold text-gray-900">
                ৳{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
            <p className="text-xs text-gray-400 text-center">
              Shipping &amp; taxes calculated at checkout
            </p>
            <button
              onClick={() => { dispatch(closeCart()); navigate('/checkout'); }}
              className="w-full py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 active:bg-primary-800 transition-colors text-sm"
            >
              Proceed to Checkout
            </button>
            <button
              onClick={() => {
                pushRemoveFromCart(cartItems, totalAmount);
                dispatch(clearCart());
              }}
              className="w-full py-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
